import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, Save, Link as LinkIcon } from 'lucide-react';
import * as Icons from '@heroicons/react/24/outline';
import ConfirmDialog from '../components/ConfirmDialog';

const sanitizeAndFormatHtml = (content) => {
    if (!content || typeof content !== 'string') return '';
    let html = content;
    html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
        const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
        if (!attrMatch) return tag;
        const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
        const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
        const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
        return `<a href="${rawUrl}"${targetAttr}>`;
    });
    if (!/<(?:br|p|div)\b/i.test(html)) {
        html = html.replace(/\r?\n/g, '<br />');
    }
    return html;
};

const DSAPartnerProgramWhoCanJoinPage = () => {
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [benefits, setBenefits] = useState([]);
    const [settings, setSettings] = useState({
        section_title: '',
        section_subtitle: '',
        cta_text: '',
        cta_link: '',
        bottom_text: 'For more information about Aham Housing Finance’s <a href="https://ahamhfc.com/aham-housing-finance-partnership-channel-partner">partnership and channel partner opportunities</a>, explore our partnership page.'
    });

    // Link inserter state
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');

    const handleInsertLink = () => {
        if (!linkUrl.trim()) {
            toast.error('Please enter a valid URL (e.g. https://ahamhfc.com/aham-housing-finance-partnership-channel-partner)');
            return;
        }
        const label = linkText.trim() || 'partnership and channel partner opportunities';
        const cleanUrl = linkUrl.trim();
        const tag = `<a href="${cleanUrl}">${label}</a>`;
        const currentValue = settings.bottom_text || '';
        const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
        setSettings(prev => ({ ...prev, bottom_text: updatedValue }));
        toast.success(`Inserted link: ${label}`);
        setShowLinkModal(false);
        setLinkText('');
        setLinkUrl('');
    };

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBenefit, setEditingBenefit] = useState(null);
    const [modalData, setModalData] = useState({
        title: '',
        description: '',
        icon_name: 'UserGroupIcon',
        gradient_from: 'blue-600',
        gradient_to: 'blue-600',
        display_order: 0,
        is_active: true
    });

    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
    const loanType = 'dsa_partner_types';

    // Available Icons for selection
    const iconOptions = [
        'UserGroupIcon',
        'BuildingOfficeIcon',
        'ChartBarIcon',
        'DocumentTextIcon',
        'HomeIcon',
        'UserIcon',
        'BriefcaseIcon',
        'ShieldCheckIcon',
        'DocumentCheckIcon'
    ];

    // Color Options (using gradient fields to store single color for now as per requirement, or we can use gradients if desired)
    // The frontend component uses text-{color} classes, so we might need to adapt or store the full class.
    // However, the seed data stored 'blue-600' in gradient_from/to.
    // Let's provide options that match the seed data style.
    const colorOptions = [
        { label: 'Blue', value: 'blue-600' },
        { label: 'Green', value: 'green-600' },
        { label: 'Purple', value: 'purple-600' },
        { label: 'Orange', value: 'orange-600' },
        { label: 'Pink', value: 'pink-600' },
        { label: 'Indigo', value: 'indigo-600' },
        { label: 'Cyan', value: 'cyan-600' },
        { label: 'Teal', value: 'teal-600' },
        { label: 'Red', value: 'red-600' },
        { label: 'Yellow', value: 'yellow-600' },
        { label: 'Lime', value: 'lime-600' },
        { label: 'Emerald', value: 'emerald-600' }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, benefitsRes] = await Promise.all([
                axios.get(`/api/admin/key-benefits/settings/${loanType}`),
                axios.get(`/api/admin/key-benefits/${loanType}`)
            ]);

            if (settingsRes.data.success) {
                const sData = settingsRes.data.data || {};
                setSettings({
                    section_title: sData.section_title || '',
                    section_subtitle: sData.section_subtitle || '',
                    cta_text: sData.cta_text || '',
                    cta_link: sData.cta_link || '',
                    bottom_text: sData.bottom_text || 'For more information about Aham Housing Finance’s <a href="https://ahamhfc.com/aham-housing-finance-partnership-channel-partner">partnership and channel partner opportunities</a>, explore our partnership page.'
                });
            }

            if (benefitsRes.data.success) {
                setBenefits(benefitsRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Settings Handlers
    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        setSavingSettings(true);
        try {
            await axios.put(`/api/admin/key-benefits/settings/${loanType}`, {
                ...settings
            });
            toast.success('Section settings saved');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    // Benefit Handlers
    const handleAddNew = () => {
        setEditingBenefit(null);
        setModalData({
            title: '',
            description: '', // Description might be empty for this section as per seed, but good to have
            icon_name: 'UserGroupIcon',
            gradient_from: 'blue-600',
            gradient_to: 'blue-600',
            display_order: benefits.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (benefit) => {
        setEditingBenefit(benefit);
        setModalData({
            title: benefit.title,
            description: benefit.description || '',
            icon_name: benefit.icon_name,
            gradient_from: benefit.gradient_from,
            gradient_to: benefit.gradient_to,
            display_order: benefit.display_order,
            is_active: benefit.is_active
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/key-benefits/${deleteConfirm.id}`);
            toast.success('Partner Type deleted successfully');
            fetchData();
        } catch (error) {
            console.error('Error deleting partner type:', error);
            toast.error('Failed to delete partner type');
        } finally {
            setDeleteConfirm({ isOpen: false, id: null });
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...modalData, loan_type: loanType };

            if (editingBenefit) {
                await axios.put(`/api/admin/key-benefits/${editingBenefit.id}`, payload);
                toast.success('Partner Type updated successfully');
            } else {
                await axios.post('/api/admin/key-benefits', payload);
                toast.success('Partner Type added successfully');
            }

            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving partner type:', error);
            toast.error('Failed to save partner type');
        }
    };

    // Render Icon Helper
    const renderIcon = (iconName, className = "h-6 w-6") => {
        const IconComponent = Icons[iconName] || Icons.UserGroupIcon;
        return <IconComponent className={className} />;
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Who Can Join</h1>
                    <p className="text-slate-500">Manage partner types</p>
                </div>
            </div>

            {/* Settings Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h2 className="text-lg font-bold text-slate-800 mb-4">Section Settings</h2>
                <form onSubmit={saveSettings} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={settings.section_title}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Who Can Become a Partner?"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Subtitle</label>
                        <input
                            type="text"
                            name="section_subtitle"
                            value={settings.section_subtitle}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Anyone with a good customer network can join!"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Bottom Text (CTA)</label>
                        <input
                            type="text"
                            name="cta_text"
                            value={settings.cta_text}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="No registration fee. No prior financial experience needed."
                        />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700">
                                    Opportunities Notice Paragraph (After Cards / CTA Banner)
                                </label>
                                <p className="text-xs text-slate-500">
                                    Appears immediately after the partner-type cards / pill banner and before &ldquo;How Partnership Works&rdquo;.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowLinkModal(!showLinkModal);
                                    setLinkText('');
                                    setLinkUrl('');
                                }}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-800 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors border border-primary-200"
                            >
                                <LinkIcon className="w-3.5 h-3.5" />
                                <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
                            </button>
                        </div>

                        {showLinkModal && (
                            <div className="bg-primary-50/80 border border-primary-200 rounded-xl p-3 mb-3 space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-primary-900">Insert Link into Paragraph</span>
                                    <span className="text-[11px] text-primary-700">e.g. https://ahamhfc.com/aham-housing-finance-partnership-channel-partner</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text</label>
                                        <input
                                            type="text"
                                            value={linkText}
                                            onChange={e => setLinkText(e.target.value)}
                                            placeholder="e.g. partnership and channel partner opportunities"
                                            className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-primary-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                                        <input
                                            type="text"
                                            value={linkUrl}
                                            onChange={e => setLinkUrl(e.target.value)}
                                            placeholder="e.g. https://ahamhfc.com/aham-housing-finance-partnership-channel-partner"
                                            className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-primary-500"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={() => setShowLinkModal(false)}
                                        className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleInsertLink}
                                        className="px-3 py-1 text-xs font-bold bg-primary-600 text-white rounded-lg hover:bg-primary-700 shadow-sm"
                                    >
                                        Insert &lt;a&gt; Tag
                                    </button>
                                </div>
                            </div>
                        )}

                        <textarea
                            rows="3"
                            name="bottom_text"
                            value={settings.bottom_text || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 text-sm font-normal"
                            placeholder='For more information about Aham Housing Finance’s <a href="https://ahamhfc.com/aham-housing-finance-partnership-channel-partner">partnership and channel partner opportunities</a>, explore our partnership page.'
                        />
                        <p className="mt-1 text-[11px] text-slate-500">
                            Tip: HTML anchor tags and line breaks are fully supported. Use &ldquo;+ Insert Link&rdquo; to quickly format links.
                        </p>

                        {/* Live Preview */}
                        {settings.bottom_text && (
                            <div className="mt-3 bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3 shadow-sm">
                                <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">PARAGRAPH LIVE PREVIEW</span>
                                    </div>
                                    <span className="text-xs text-slate-500">Live preview with clickable styled link</span>
                                </div>
                                <div
                                    className="text-slate-700 text-sm md:text-base leading-relaxed [&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-700 [&_a]:font-semibold [&_a]:transition-colors"
                                    dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(settings.bottom_text) }}
                                />
                            </div>
                        )}
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={savingSettings}
                            className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700 transition-colors"
                        >
                            <Save size={18} />
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            </div>
            {/* Note: Settings might not be strictly needed if the title is static in the design, but good to keep if we want it dynamic. 
                The user asked for "all the data edit option", so keeping it is safer.
            */}

            {/* Partner Types List */}
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-bold text-slate-800">Partner Types</h2>
                    <button
                        onClick={handleAddNew}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors"
                    >
                        <Plus size={18} /> Add New
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {benefits.map((benefit) => (
                        <div key={benefit.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                            <div className={`w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-4 text-${benefit.gradient_from}`}>
                                {renderIcon(benefit.icon_name)}
                            </div>
                            <h3 className="font-bold text-slate-800 mb-2">{benefit.title}</h3>
                            {/* <p className="text-sm text-slate-500 mb-4 line-clamp-3">{benefit.description}</p> */}
                            <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                                <span className={`text-xs font-medium px-2 py-1 rounded-lg ${benefit.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                    {benefit.is_active ? 'Active' : 'Inactive'}
                                </span>
                                <div className="flex gap-2">
                                    <button onClick={() => handleEdit(benefit)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                                        <Pencil size={16} />
                                    </button>
                                    <button onClick={() => handleDelete(benefit.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Edit/Add Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                        <h2 className="text-xl font-bold mb-6">{editingBenefit ? 'Edit Partner Type' : 'Add New Partner Type'}</h2>
                        <form onSubmit={handleModalSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Title</label>
                                <input
                                    type="text"
                                    value={modalData.title}
                                    onChange={(e) => setModalData({ ...modalData, title: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-xl"
                                    required
                                />
                            </div>
                            {/* <div>
                                <label className="block text-sm font-medium mb-1">Description</label>
                                <textarea
                                    value={modalData.description}
                                    onChange={(e) => setModalData({ ...modalData, description: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-xl"
                                    rows={3}
                                />
                            </div> */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Icon</label>
                                    <select
                                        value={modalData.icon_name}
                                        onChange={(e) => setModalData({ ...modalData, icon_name: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                    >
                                        {iconOptions.map(icon => (
                                            <option key={icon} value={icon}>{icon}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Color</label>
                                    <select
                                        value={modalData.gradient_from}
                                        onChange={(e) => {
                                            setModalData({
                                                ...modalData,
                                                gradient_from: e.target.value,
                                                gradient_to: e.target.value
                                            });
                                        }}
                                        className="w-full px-4 py-2 border rounded-xl"
                                    >
                                        {colorOptions.map(c => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={modalData.display_order}
                                        onChange={(e) => setModalData({ ...modalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                    />
                                </div>
                                <div className="flex items-center h-full pt-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={modalData.is_active}
                                            onChange={(e) => setModalData({ ...modalData, is_active: e.target.checked })}
                                            className="w-5 h-5 rounded text-primary-600"
                                        />
                                        <span className="font-medium">Active</span>
                                    </label>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2 border rounded-xl hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Partner Type"
                message="Are you sure you want to delete this partner type?"
            />
        </div>
    );
};

export default DSAPartnerProgramWhoCanJoinPage;
